/**
 * Note Renderer
 *
 * Draws notes falling down the lanes
 */

import { Note } from '../gameplay/Note';

export class NoteRenderer {
  private readonly NOTE_SIZE: number = 40;
  private readonly LANE_WIDTH_RATIO: number = 0.6;
  private readonly NUM_LANES: number = 4;

  /**
   * Render all visible notes
   */
  render(ctx: CanvasRenderingContext2D, notes: Note[]): void {
    const canvas = ctx.canvas;
    const laneAreaWidth = canvas.width * this.LANE_WIDTH_RATIO;
    const laneWidth = laneAreaWidth / this.NUM_LANES;
    const laneStartX = (canvas.width - laneAreaWidth) / 2;

    for (const note of notes) {
      const x = laneStartX + (note.lane * laneWidth) + (laneWidth / 2);
      const y = this.calculateNoteY(canvas.height, note.position);

      this.drawNote(ctx, x, y, note.lane);
    }
  }

  /**
   * Calculate Y position based on note's normalized position (0 = top, 1 = judgment line)
   */
  private calculateNoteY(canvasHeight: number, position: number): number {
    const judgmentLineY = canvasHeight * 0.8;
    return position * judgmentLineY;
  }

  /**
   * Draw a single note
   */
  private drawNote(ctx: CanvasRenderingContext2D, x: number, y: number, lane: number): void {
    const colors = [
      '#ff6b9d', // Pink
      '#4ecdc4', // Cyan
      '#ffe66d', // Yellow
      '#a8e6cf'  // Green
    ];

    // Note glow
    ctx.shadowBlur = 10;
    ctx.shadowColor = colors[lane % colors.length];

    // Note circle
    ctx.fillStyle = colors[lane % colors.length];
    ctx.beginPath();
    ctx.arc(x, y, this.NOTE_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();

    // Note border
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Reset shadow
    ctx.shadowBlur = 0;
  }

  /**
   * Draw hit effect (called when note is hit)
   */
  drawHitEffect(ctx: CanvasRenderingContext2D, x: number, y: number, judgment: string): void {
    const colors: { [key: string]: string } = {
      'PERFECT': '#ffd700',
      'GREAT': '#00ff88',
      'GOOD': '#4ecdc4',
      'MISS': '#ff6b6b'
    };

    const color = colors[judgment] || '#ffffff';

    // Expanding circle effect
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.shadowBlur = 20;
    ctx.shadowColor = color;

    ctx.beginPath();
    ctx.arc(x, y, this.NOTE_SIZE, 0, Math.PI * 2);
    ctx.stroke();

    ctx.shadowBlur = 0;
  }
}
